import { useEffect } from "react";
import type { SiteLocale } from "@/hooks/useRouteLocale";

export interface PageMeta {
  /** Full document title. */
  title: string;
  description: string;
  locale: SiteLocale;
  /** Locale-prefixed path of this page, used for canonical and og:url. */
  path: string;
  /** Same page in each locale (hreflang alternates). */
  alternates?: Partial<Record<SiteLocale, string>>;
  type?: "website" | "article";
  /** Adds robots "noindex" (e.g. the not-found state of an article route). */
  noindex?: boolean;
  /** Absolute URL of the share image (og:image); omitted when the page has none. */
  image?: string;
}

const OG_LOCALE: Record<SiteLocale, string> = { ro: "ro_RO", en: "en_US" };

type Restore = () => void;

/**
 * Sets an attribute on the head element matched by `selector`, creating the
 * element when absent. Returns how to undo exactly that change.
 */
function setHeadElement(
  selector: string,
  create: () => HTMLElement,
  attribute: string,
  value: string,
): Restore {
  const existing = document.head.querySelector<HTMLElement>(selector);
  if (existing) {
    const previous = existing.getAttribute(attribute);
    existing.setAttribute(attribute, value);
    return () => {
      if (previous === null) existing.removeAttribute(attribute);
      else existing.setAttribute(attribute, previous);
    };
  }
  const element = create();
  element.setAttribute(attribute, value);
  document.head.appendChild(element);
  return () => element.remove();
}

function metaElement(key: "name" | "property", name: string): () => HTMLElement {
  return () => {
    const element = document.createElement("meta");
    element.setAttribute(key, name);
    return element;
  };
}

function linkElement(rel: string, hreflang?: string): () => HTMLElement {
  return () => {
    const element = document.createElement("link");
    element.setAttribute("rel", rel);
    if (hreflang) element.setAttribute("hreflang", hreflang);
    return element;
  };
}

/**
 * Sets the document title, description, Open Graph tags, canonical link and
 * RO/EN hreflang alternates for a client-rendered page, and puts back what
 * was there before when the page unmounts or the values change.
 *
 * The app has no SSR/prerender, so crawlers that do not execute JavaScript
 * only see the static index.html tags.
 */
export function usePageMeta(meta: PageMeta): void {
  const { title, description, locale, path, type = "website", noindex = false, image } = meta;
  const roAlternate = meta.alternates?.ro;
  const enAlternate = meta.alternates?.en;

  useEffect(() => {
    const origin = window.location.origin;
    const url = `${origin}${path}`;
    const restores: Restore[] = [];

    const previousTitle = document.title;
    document.title = title;
    restores.push(() => {
      document.title = previousTitle;
    });

    const set = (selector: string, create: () => HTMLElement, attribute: string, value: string) =>
      restores.push(setHeadElement(selector, create, attribute, value));

    set('meta[name="description"]', metaElement("name", "description"), "content", description);
    set('meta[property="og:title"]', metaElement("property", "og:title"), "content", title);
    set('meta[property="og:description"]', metaElement("property", "og:description"), "content", description);
    set('meta[property="og:type"]', metaElement("property", "og:type"), "content", type);
    set('meta[property="og:url"]', metaElement("property", "og:url"), "content", url);
    set('meta[property="og:locale"]', metaElement("property", "og:locale"), "content", OG_LOCALE[locale]);
    set('link[rel="canonical"]', linkElement("canonical"), "href", url);

    if (roAlternate) {
      set('link[rel="alternate"][hreflang="ro"]', linkElement("alternate", "ro"), "href", `${origin}${roAlternate}`);
      // Romanian is the default locale of the site.
      set('link[rel="alternate"][hreflang="x-default"]', linkElement("alternate", "x-default"), "href", `${origin}${roAlternate}`);
    }
    if (enAlternate) {
      set('link[rel="alternate"][hreflang="en"]', linkElement("alternate", "en"), "href", `${origin}${enAlternate}`);
    }
    if (noindex) {
      set('meta[name="robots"]', metaElement("name", "robots"), "content", "noindex");
    }
    if (image) {
      set('meta[property="og:image"]', metaElement("property", "og:image"), "content", image);
      set('meta[name="twitter:card"]', metaElement("name", "twitter:card"), "content", "summary_large_image");
    }

    return () => {
      for (let i = restores.length - 1; i >= 0; i -= 1) restores[i]();
    };
  }, [title, description, locale, path, type, noindex, image, roAlternate, enAlternate]);
}
